import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Biome",
  "description": "Learn how to use Biome in your Turborepo projects."
};
export let structuredData = {
  "contents": [{
    "heading": "",
    "content": "Biome is a fast formatter for JavaScript, TypeScript, JSX, and JSON that saves CI and developer time."
  }, {
    "heading": "using-biome-with-turborepo",
    "content": "Biome is a rare exception to most tools that are used with Turborepo because it is so extraordinarily fast. For this reason, we recommend using a Root Task rather than creating separate scripts in each of your packages."
  }, {
    "heading": "using-biome-with-turborepo",
    "content": "Using Biome at the root of the project will result in cache misses for all\ntasks when you upgrade Biome versions or change configuration. If you prefer\nthe tradeoff of higher cache hit ratios in these situations over less\nconfiguration, you can still use Biome in separate scripts like the other\nrecommendations in our guides."
  }, {
    "heading": "initialize-biome",
    "content": "First, follow the installation documentation to set up Biome in your repository. You'll then be able to create a script to use Biome in the root of your repository:"
  }, {
    "heading": "create-a-root-task",
    "content": "In practice, Biome is unlikely to be a bottleneck in the iteration speed of your repository. For this reason, we can have less configuration to manage in our repository by using Biome in a Root Task."
  }, {
    "heading": "create-a-root-task",
    "content": "If you believe Biome may be faster in your repository split up into tasks in\npackages, you are free to do so. We encourage you to experiment with what's\nbest for your use case."
  }, {
    "heading": "create-a-root-task",
    "content": "To create a Root Task, register the scripts to Turborepo:"
  }, {
    "heading": "create-a-root-task",
    "content": "You'll now be able to run these scripts using turbo run format-and-lint and turbo run format-and-lint:fix."
  }],
  "headings": [{
    "id": "using-biome-with-turborepo",
    "content": "Using Biome with Turborepo"
  }, {
    "id": "initialize-biome",
    "content": "Initialize Biome"
  }, {
    "id": "create-a-root-task",
    "content": "Create a root task"
  }]
};
import {Callout} from '#components/callout';
import {CreateTurboCallout} from './create-turbo-callout.tsx';
export const toc = [{
  depth: 2,
  url: "#using-biome-with-turborepo",
  title: _jsx(_Fragment, {
    children: "Using Biome with Turborepo"
  })
}, {
  depth: 3,
  url: "#initialize-biome",
  title: _jsx(_Fragment, {
    children: "Initialize Biome"
  })
}, {
  depth: 3,
  url: "#create-a-root-task",
  title: _jsx(_Fragment, {
    children: "Create a root task"
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h2: "h2",
    h3: "h3",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://biomejs.dev/",
        children: "Biome"
      }), " is a fast formatter for JavaScript, TypeScript, JSX, and JSON that saves CI and developer time."]
    }), "\n", _jsx(CreateTurboCallout, {}), "\n", _jsx(_components.h2, {
      id: "using-biome-with-turborepo",
      children: "Using Biome with Turborepo"
    }), "\n", _jsxs(_components.p, {
      children: ["Biome is a rare exception to most tools that are used with Turborepo because it is ", _jsx(_components.strong, {
        children: "so extraordinarily fast"
      }), ". For this reason, we recommend using a ", _jsx(_components.a, {
        href: "/docs/crafting-your-repository/configuring-tasks#registering-root-tasks",
        children: "Root Task"
      }), " rather than creating separate scripts in each of your packages."]
    }), "\n", _jsx(Callout, {
      type: "info",
      title: "Caching behavior",
      children: _jsx(_components.p, {
        children: "Using Biome at the root of the project will result in cache misses for all\ntasks when you upgrade Biome versions or change configuration. If you prefer\nthe tradeoff of higher cache hit ratios in these situations over less\nconfiguration, you can still use Biome in separate scripts like the other\nrecommendations in our guides."
      })
    }), "\n", _jsx(_components.h3, {
      id: "initialize-biome",
      children: "Initialize Biome"
    }), "\n", _jsxs(_components.p, {
      children: ["First, ", _jsx(_components.a, {
        href: "https://biomejs.dev/guides/getting-started/",
        children: "follow the installation documentation to set up Biome"
      }), " in your repository. You'll then be able to create a script to use Biome in the root of your repository:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./package.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"scripts\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"format-and-lint\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"biome check .\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"format-and-lint:fix\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"biome check . --write\""
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.h3, {
      id: "create-a-root-task",
      children: "Create a root task"
    }), "\n", _jsxs(_components.p, {
      children: ["In practice, Biome is unlikely to be a bottleneck in the iteration speed of your repository. For this reason, we can have less configuration to manage in our repository by using Biome in a ", _jsx(_components.a, {
        href: "/docs/crafting-your-repository/configuring-tasks#registering-root-tasks",
        children: "Root Task"
      }), "."]
    }), "\n", _jsx(Callout, {
      type: "info",
      children: _jsx(_components.p, {
        children: "If you believe Biome may be faster in your repository split up into tasks in\npackages, you are free to do so. We encourage you to experiment with what's\nbest for your use case."
      })
    }), "\n", _jsxs(_components.p, {
      children: ["To create a ", _jsx(_components.a, {
        href: "/docs/crafting-your-repository/configuring-tasks#registering-root-tasks",
        children: "Root Task"
      }), ", register the scripts to Turborepo:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"tasks\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"//#format-and-lint\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {}"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"//#format-and-lint:fix\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"cache\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: " false"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["You'll now be able to run these scripts using ", _jsx(_components.code, {
        children: "turbo run format-and-lint"
      }), " and ", _jsx(_components.code, {
        children: "turbo run format-and-lint:fix"
      }), "."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
